/* eslint-disable no-const-assign */
/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from "react";
import SearchField from "./DataTableSearch";
import DataTable, { createTheme } from "react-data-table-component";
import { toSnake } from "./ReqResConvertCase";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Button } from "reactstrap";
import TableSkeleton from "./Skeleton";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Row } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './DataTable.css';

const CommonDataTable = (props) => {
  const layoutModeType = useSelector((state) => state.Layout.layoutModeType);

  createTheme("dark", {
    background: {
      default: "transparent",
    },
  });

  const [filterParams, setFilterParams] = useState(props.filterParams || {});
  const [clearSelectedRowsFlag, setClearSelectedRowsFlag] = useState(false);
  const dataRef = useRef(props.data);

  useEffect(() => {
    setFilterParams(props.filterParams || {});
  }, [props.filterParams]);

  useEffect(() => {
    dataRef.current = props.data;
  }, [props.data]);


  const updateFilterParams = async (newFilterParams) => {
    setFilterParams(newFilterParams);
    await props.fetchData(newFilterParams);
  };

  const exportToExcel = async (e) => {
    e.preventDefault(); // Prevent form submission
    // Set limit high to fetch all records, and reset page to 1. Merge props.filterParams to be safe.
    const newParams = { ...props.filterParams, ...filterParams, isDownload: true, limit: 1000000, page: 1 };
    // Update filterParams to fetch all data
    await updateFilterParams(newParams);

    try {
      // Wait for data to be fetched and available in props.data
      await new Promise((resolve, reject) => {
        const interval = setInterval(() => {
          const currentData = dataRef.current;
          if (currentData && currentData.length > 0) {
            // Check if this is a users table with LT fields
            const hasLTFields = currentData.some(user => user.ltTotalSpent !== undefined);

            if (!hasLTFields) {
              // Not a users table, proceed immediately
              clearInterval(interval);
              resolve();
              return;
            }

            // For users table, wait for ALL users' LT stats to be populated
            // For users table, wait for ALL users' LT stats to be populated
            const allUsersLoaded = currentData.every(user => {
              // Check if user has been processed (has LT fields, even if 0)
              return user.hasOwnProperty('ltTotalSpent') && user.hasOwnProperty('ltTotalWon') &&
                user.hasOwnProperty('ltGamesPlayed') && user.hasOwnProperty('ltGamesWon');
            });

            // Also check for LT1 fields if present
            const hasLT1Fields = currentData.some(user => user.lt1TotalSpent !== undefined);
            let allLT1UsersLoaded = true;
            if (hasLT1Fields) {
              allLT1UsersLoaded = currentData.every(user => {
                return user.hasOwnProperty('lt1TotalSpent') && user.hasOwnProperty('lt1TotalWon') &&
                  user.hasOwnProperty('lt1GamesPlayed') && user.hasOwnProperty('lt1GamesWon');
              });
            }

            // Proceed when all users have their stats fields populated
            if (allUsersLoaded && allLT1UsersLoaded) {
              clearInterval(interval);
              resolve();
            }
          }
        }, 500);

        // Fallback timeout after 15 seconds
        setTimeout(() => {
          clearInterval(interval);
          resolve();
        }, 15000);
      });

      // Create worksheet from full data (using REF)
      const dataToExport = (dataRef.current || []).map(row => {
        const filteredRow = { ...row };

        // Format winners array for export
        if (Array.isArray(row.winners)) {
          filteredRow.winners = row.winners.map(w => {
            if (typeof w === 'object' && w !== null) {
              return w.email || w.userId || w.name || '';
            }
            return w;
          }).filter(Boolean).join(', ');
        }

        if (props.excludeExportColumns && Array.isArray(props.excludeExportColumns)) {
          props.excludeExportColumns.forEach(field => {
            delete filteredRow[field];
          });
        }
        return filteredRow;
      });

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      XLSX.writeFile(wb, `${props.exportFileName || "data"}.xlsx`, {
        bookType: "xlsx",
        type: "buffer",
        compression: true,
      });

      // Reset the isDownload param
      const resetParams = { ...filterParams, isDownload: false };
      updateFilterParams(resetParams);
    } catch (error) {
      updateFilterParams({ ...filterParams, isDownload: false });
    }
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const data = Array.isArray(props.data) ? props.data : [];

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = props.data.map(row => row.id);
      setSelectedIds(allIds);
      props.bulkSelected && props.bulkSelected(props.data);
    } else {
      setSelectedIds([]);
      props.bulkSelected && props.bulkSelected([]);
    }
  };

  const handleSelectRow = (rowId, row) => {
    const newSelectedIds = selectedIds.includes(rowId)
      ? selectedIds.filter(id => id !== rowId)
      : [...selectedIds, rowId];

    setSelectedIds(newSelectedIds);

    if (props.bulkSelected) {
      const selectedRows = props.data.filter(row => newSelectedIds.includes(row.id));
      props.bulkSelected(selectedRows);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination || !props.onRowReorder) return;

    const items = Array.from(data);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    props.onRowReorder(items);
  };

  const DraggableTable = () => {

    if (!props.data || props.data.length === 0) {
      console.warn("No data to display in DraggableTable");
    }
    return (
      <div className="draggable-table-wrapper">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="table-header">
            {props.checkboxEnabled && (
              <div className="header-cell checkbox-cell">
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  checked={selectedIds.length === props.data.length}
                />
              </div>
            )}
            <div className="header-cell drag-handle-cell"></div>
            {props.columns.map((column, index) => (
              <div key={index} className={`header-cell ${column.name.props.children === 'Actions' ? 'gender-cell' : ''}`}>
                {typeof column.name === 'string' ? (
                  <span className="font-weight-bold fs-13">{column.name}</span>
                ) : (
                  column.name
                )}
              </div>
            ))}
          </div>
          <Droppable droppableId="table">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="draggable-table-container"
              >
                {props.data.map((row, index) => (
                  <Draggable
                    key={row.id?.toString() || `row-${index}`}
                    draggableId={row.id?.toString() || `row-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`draggable-row ${snapshot.isDragging ? 'dragging' : ''}`}
                      >
                        {props.checkboxEnabled && (
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.id)}
                            disabled={!!row.isDeleted} // ✅ Disable checkbox if user is soft deleted
                            onChange={() => {
                              if (!row.isDeleted) handleSelectRow(row.id, row);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div {...provided.dragHandleProps} className="drag-handle">
                          <i className="ri-drag-move-2-fill" />
                        </div>
                        {props.columns.map((column, colIndex) => (
                          <div key={colIndex} className={`table-cell ${column.name.props.children === 'Actions' ? 'question-cell' : ''}`}>
                            {column.cell
                              ? column.cell(row)
                              : typeof column.selector === "function"
                                ? column.selector(row)
                                : row[column.selector]}
                          </div>
                        ))}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  };

  const handleRowsPerPageChange = (newPerPage, page) => {
    const updatedFilterParams = {
      ...filterParams,
      currentPage: page,
      page,
      perPage: newPerPage,
      limit: newPerPage, // Also send limit for backend
    };
    setFilterParams(updatedFilterParams);
    if (props.fetchData) {
      props.fetchData(updatedFilterParams);
    }
  };

  const handleSort = (column, sortDirection) => {
    const sortField = column?.sortField || column?.selector;
    if (!sortField) return;

    const order = sortDirection || 'desc';

    let orderBy = sortField;
    if (typeof sortField === 'function') {
      const match = sortField.toString().match(/row\s*=>\s*row\.([\w.?]+)/);
      orderBy = match ? match[1] : '';
    }
    if (typeof orderBy !== 'string') {
      orderBy = '';
    }
    orderBy = orderBy.replace(/\?.*$/, '').replace(/\(\)/g, '').trim();
    if (!orderBy) {
      return;
    }

    const updatedFilterParams = {
      ...filterParams,
      order,
      sort: order,
      orderBy: orderBy, // Field name like createdAt, email, etc.
    };

    setFilterParams(updatedFilterParams);
    if (props.fetchData) {
      props.fetchData(updatedFilterParams);
    }
  };

  const handlePageChange = (page) => {
    const updatedFilterParams = {
      ...filterParams,
      currentPage: page,
      page: page,
    };
    setFilterParams(updatedFilterParams);
    if (props.fetchData) {
      props.fetchData(updatedFilterParams);
    }
  };

  useEffect(() => {
    if (Array.isArray(props.selectedRows) && props.selectedRows.length === 0) {
      setClearSelectedRowsFlag((prev) => !prev);
    }
  }, [props.selectedRows]);

  return (
    <Row>
      <Col lg={12}>
        <Card style={{ marginTop: "35px", marginBottom: "60px" }} className={props.cardcusclass}>
          <CardBody>
            <Row className="g-4">
              <div className={`col-sm ${props.callcusclass}`}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start", // for better vertical alignment
                    gap: "10px",
                    flexWrap: "wrap",
                    position: "relative",
                  }}
                >
                  <div style={{
                    display: "flex",
                    alignItems: "flex-start", // for better vertical alignment
                    gap: "10px",
                    flexWrap: "wrap",
                  }}>
                    {props.searchEnable && (
                      <div className="form-icon" style={{ flex: "1", maxWidth: "300px" }}>
                        <SearchField
                          filterParams={filterParams}
                          fetchData={props.fetchData}
                        />
                      </div>
                    )}
                    {props.customFilters && (
                      <div style={{ flex: "0 1" }} className={props.customFiltersClassName}>
                        {props.customFilters}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "10px" }} className="mb-3">
                    {props.showRefreshButton && (
                      <Button
                        className="btn btn-primary"
                        color="primary"
                        type="button"
                        onClick={props.onRefresh}
                        disabled={props.refreshLoading}
                      >
                        {props.refreshLoading ? (
                          <i className="ri-loader-4-line ri-spin"></i>
                        ) : (
                          <i className="ri-refresh-line"></i>
                        )}
                        {' '}Refresh
                      </Button>
                    )}
                    {props.showAddButton && (
                      <Button
                        className="btn btn-primary"
                        color="primary"
                        type="submit"
                        onClick={props.addOnClickMethod}
                      >
                        <i className="ri-add-line"></i>
                        {`${props.buttonName ? props.buttonName : "New"}`}
                      </Button>
                    )}
                    {props.showExportButton && (
                      <Button
                        className="btn btn-primary"
                        color="primary"
                        type="submit"
                        onClick={props.onExport || exportToExcel}
                        disabled={props.isExporting}
                      >
                        {props.isExporting ? (
                          <>
                            Exporting...
                          </>
                        ) : (
                          <>
                            <i className="bx bxs-file-export"></i>Export
                          </>
                        )}
                      </Button>
                    )}
                    {props.selectedRows?.length > 0 && (
                      <Button
                        className="btn btn-danger"
                        color="danger"
                        onClick={props.bulkDelete}
                      >
                        Delete Item
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </Row>
            <div className={`table-container table-responsive table-card ${props.onRowReorder ? 'draggable' : ''}`}>
              {props.onRowReorder ? (
                <DraggableTable />
              ) : (
                <DataTable
                  theme={layoutModeType === "light" ? "" : "dark"}
                  columns={props.columns}
                  data={props.data}
                  pagination
                  highlightOnHover
                  paginationServer
                  selectableRows={props.checkboxEnabled}
                  clearSelectedRows={clearSelectedRowsFlag}
                  progressComponent={
                    <div style={{ width: "100%" }}>
                      <TableSkeleton rows={10} columns={props.columns.length} />
                    </div>
                  }
                  progressPending={props.loading}
                  onChangeRowsPerPage={handleRowsPerPageChange}
                  sortServer={true}
                  pointerOnHover
                  selectableRowDisabled={props.selectableRowDisabled}
                  onRowClicked={props.onRowClicked || props.onRowClick}
                  persistTableHead
                  conditionalRowStyles={props.conditionalRowStyles || []}
                  paginationPerPage={
                    filterParams.perPage || filterParams.limit || process.env.REACT_APP_RECORDS_PER_PAGE || 10
                  }
                  paginationDefaultPage={filterParams.page || filterParams.currentPage || 1}
                  paginationTotalRows={props.totalRows}
                  onSelectedRowsChange={({ selectedRows }) => {
                    if (props.bulkSelected) {
                      props.bulkSelected(selectedRows);
                    }
                  }}
                  onSort={handleSort}
                  onChangePage={handlePageChange}
                />
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default CommonDataTable;
